import { useEffect, useState } from 'react';

import { contrastForVar } from './contrast';

interface SemanticPair {
  bgVar: string;
  fgVar: string;
  label: string;
  description: string;
}

interface UsageEntry {
  role: string;
  use: string;
  avoid?: string;
}

const SEMANTIC_PAIRS: SemanticPair[] = [
  {
    bgVar: 'success',
    fgVar: 'success-foreground',
    label: 'Success',
    description: 'Completed actions, healthy or up-to-date data, positive deltas.',
  },
  {
    bgVar: 'warning',
    fgVar: 'warning-foreground',
    label: 'Warning',
    description: 'Non-blocking advisories, soft limits approached, recoverable issues.',
  },
  {
    bgVar: 'destructive',
    fgVar: 'destructive-foreground',
    label: 'Destructive',
    description: 'Errors, irreversible actions (delete, suspend), validation failures.',
  },
  {
    bgVar: 'muted',
    fgVar: 'muted-foreground',
    label: 'Muted',
    description: 'Informational text, helper copy, placeholder text, empty states.',
  },
  {
    bgVar: 'accent',
    fgVar: 'accent-foreground',
    label: 'Accent',
    description: 'Selection or hover affordances. Never used to convey state.',
  },
];

const USAGE_GUIDE: UsageEntry[] = [
  { role: 'success', use: 'Completed actions, healthy or up-to-date data, positive deltas.' },
  {
    role: 'warning',
    use: 'Non-blocking advisories, soft limits approached, recoverable issues.',
  },
  {
    role: 'destructive',
    use: 'Errors, irreversible actions (delete, suspend), validation failures.',
  },
  { role: 'muted', use: 'Informational text, helper copy, placeholder text, empty states.' },
  {
    role: 'accent',
    use: 'Selection or hover affordances.',
    avoid: 'Never used to convey state.',
  },
];

function ContrastBadge({ ratio }: Readonly<{ ratio: number | null }>) {
  if (ratio == null) return <span className="text-muted-foreground text-xs">—</span>;
  const passes = ratio >= 4.5;
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono text-xs ${
        passes ? 'text-foreground' : 'text-destructive'
      }`}
    >
      {ratio.toFixed(2)}:1 · {passes ? 'passes AA' : 'fails AA'}
    </span>
  );
}

function SemanticPairTile({ pair }: Readonly<{ pair: SemanticPair }>) {
  const [ratio, setRatio] = useState<number | null>(null);
  useEffect(() => {
    setRatio(contrastForVar(pair.bgVar, pair.fgVar));
  }, [pair.bgVar, pair.fgVar]);
  return (
    <div className="space-y-2">
      <div
        className="border-border flex h-20 items-center justify-center rounded-md border"
        style={{
          backgroundColor: `hsl(var(--${pair.bgVar}))`,
          color: `hsl(var(--${pair.fgVar}))`,
        }}
      >
        <span className="text-sm font-semibold">{pair.label}</span>
      </div>
      <p className="text-foreground font-mono text-xs">
        {pair.bgVar} <span className="text-muted-foreground">+</span> {pair.fgVar}
      </p>
      <p className="text-muted-foreground text-xs">{pair.description}</p>
      <ContrastBadge ratio={ratio} />
    </div>
  );
}

export function SemanticStatesSection() {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Semantic states</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Five role pairs that convey state. Every consumer must pair the role with its declared
          foreground; arbitrary text colours on a coloured surface are not allowed. Contrast ratios
          are computed at runtime against WCAG AA (≥ 4.5:1 for normal text).
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {SEMANTIC_PAIRS.map((pair) => (
          <SemanticPairTile key={pair.bgVar} pair={pair} />
        ))}
      </div>
    </section>
  );
}

export function UsageGuideSection() {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Usage guide</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Which role to reach for. The semantic vocabulary is uniform: a success in the trades area
          looks the same as a success in claims.
        </p>
      </div>
      <dl className="border-border divide-border grid grid-cols-1 divide-y rounded-md border">
        {USAGE_GUIDE.map((entry) => (
          <div key={entry.role} className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-[8rem_1fr]">
            <dt className="text-foreground font-mono text-sm">{entry.role}</dt>
            <dd className="text-foreground text-sm">
              <span>{entry.use}</span>
              {entry.avoid && <span className="text-muted-foreground"> {entry.avoid}</span>}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function PatternsSection() {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Patterns</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          One example of each role in a realistic surface. Toast, banner, and alert components
          themselves live in feature scopes; these are inline demos only.
        </p>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="bg-success text-success-foreground inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold">
            Approved
          </span>
          <span className="text-muted-foreground font-mono text-xs">success badge</span>
        </div>
        <div className="bg-warning text-warning-foreground flex items-center gap-2 rounded-md px-3 py-2 text-sm">
          <span aria-hidden>⚠︎</span>
          <span>Credit limit will exceed the threshold by Friday.</span>
          <span className="ml-auto font-mono text-xs opacity-70">warning banner</span>
        </div>
        <div className="bg-destructive text-destructive-foreground flex items-center gap-2 rounded-md px-3 py-2 text-sm">
          <span aria-hidden>✕</span>
          <span>Failed to save changes. Please retry.</span>
          <span className="ml-auto font-mono text-xs opacity-70">destructive callout</span>
        </div>
        <div className="text-muted-foreground text-sm">
          Helper copy uses <span className="font-mono">muted-foreground</span> against the page
          background.
        </div>
        <div className="bg-accent text-accent-foreground flex items-center gap-2 rounded-md px-3 py-2 text-sm">
          <span aria-hidden>●</span>
          <span>Selected row · Q1 financials</span>
          <span className="ml-auto font-mono text-xs opacity-70">accent (selection)</span>
        </div>
      </div>
    </section>
  );
}
